import Dexie from 'dexie'
import { formatDateToISOShort } from '../common/utilities/FormatDate'

const db = new Dexie('CorporateExperienceLobby')

// Ventana de relevancia para la réplica local offline-first: sincronizar y
// conservar solo los últimos SYNC_WINDOW_DAYS días de `visits`/`invitations`.
// El historial completo sigue disponible online vía búsqueda puntual indexada
// en el backend (ver docs/local-first.md, sección "Ventana de 90 días").
export const SYNC_WINDOW_DAYS = 90

db.version(1).stores({
  // Visitas: clave primaria = uuid (generado por el cliente)
  // id = id del servidor (null hasta sincronizar)
  // syncStatus: 'pending' | 'synced' | 'failed'
  // syncError: motivo de rechazo del backend cuando syncStatus === 'failed' (ver SyncEngine.pushSingleVisit)
  // pushAttempts: ciclos de sync consecutivos fallidos (red/5xx) para esta visita;
  //   se resetea a 0 en cada exito y al restaurar desde cuarentena (ver SyncEngine.MAX_PUSH_ATTEMPTS)
  visits: 'uuid, id, building_id, entry_time, syncStatus, updated_at',

  // Invitaciones/pre-registros: solo lectura desde la app
  // _delete: boolean — si true, fue eliminada desde otro producto
  invitations: 'id, building_id, date, _delete',

  // Empleados para el autocomplete de host
  employees: 'id, email, first_name, last_name',

  // Lista negra para validación offline en el formulario
  black_lists: 'id, identification',

  // Orígenes para el autocomplete "comes_from"
  comes_from: 'id, name',

  // Checkpoints de sincronización por entidad
  // { entity: string, lastCheckpoint: string (ISO date) }
  sync_meta: 'entity',
})

// Visitas que aún no se habían sincronizado (syncStatus 'pending' o 'failed')
// al momento de cerrar sesión. `clearLocalDatabase` las mueve aquí en lugar de
// borrarlas, y `LoadPage` le pregunta al usuario en el próximo login si desea
// reintentar sincronizarlas o descartarlas (ver PendingVisitsModal).
db.version(2).stores({
  quarantined_visits: 'uuid, syncStatus, quarantinedAt',
})

// Solo estos estados representan datos que el servidor nunca confirmó — son
// los únicos que sobreviven a un `clearLocalDatabase()`, movidos a cuarentena.
// Exportado para que cualquier otro flujo destructivo (p.ej. `syncVisitsForBuilding`
// en SyncEngine, o el guard de cambio de edificio en VisitorsFilter) use la misma
// fuente de verdad sobre qué cuenta como "sin sincronizar".
export const UNSYNCED_VISIT_STATUSES = ['pending', 'failed']

db.version(3).stores({
  visits: 'uuid, id, building_id, entry_time, syncStatus, updated_at',
  invitations: 'id, building_id, date, syncStatus, updated_at, _delete',
  employees: 'id, email, first_name, last_name',
  black_lists: 'id, identification',
  comes_from: 'id, name',
  sync_meta: 'entity',
}).upgrade(async tx => {
  await tx.table('invitations').toCollection().modify(invitation => {
    invitation.syncStatus = invitation.syncStatus || 'synced'
  })
})

// Indexa `unique_identificator` (código embebido en el QR de la invitación,
// prefijo `parso_`) para permitir una búsqueda offline eficiente por QR
// (`findInvitationByQRCode`) en vez de un `.filter()` de tabla completa.
db.version(4).stores({
  visits: 'uuid, id, building_id, entry_time, syncStatus, updated_at',
  invitations: 'id, building_id, date, unique_identificator, syncStatus, updated_at, _delete',
  employees: 'id, email, first_name, last_name',
  black_lists: 'id, identification',
  comes_from: 'id, name',
  sync_meta: 'entity',
})

// Invitaciones que aún no se habían sincronizado (syncStatus 'pending' o
// 'failed') al momento de cerrar sesión. Mismo patrón que `quarantined_visits`:
// `clearLocalDatabase` las mueve aquí en lugar de borrarlas, y `LoadPage` le
// pregunta al usuario en el próximo login si desea reintentar sincronizarlas
// o descartarlas (ver PendingInvitationsModal).
export const UNSYNCED_INVITATION_STATUSES = ['pending', 'failed']

db.version(5).stores({
  quarantined_invitations: 'id, syncStatus, quarantinedAt',
})

// Cola de fotos (visita e identificación) pendientes de subir.
//
// Las fotos NO pueden viajar con la visita en `/visits/batch`: el backend las
// recibe en endpoints aparte (`POST /api/visit/photo/:id`,
// `POST /api/visit/credential_photo/:id`) que resuelven la visita con
// `Visit.find(params[:id])` — es decir, exigen el id NUMÉRICO del servidor, no
// el uuid del cliente. Y ese id solo aparece en el *pull* del sync: el POST a
// `/visits/batch` devuelve únicamente contadores, nunca los registros creados.
//
// Por eso el blob se persiste aquí en el momento de la captura y `SyncEngine`
// lo sube después del pull, cuando la visita local ya tiene `id`. Esto además
// permite capturar fotos sin conexión y hace que un upload fallido se
// reintente en vez de perderse con el estado de React.
//
// key: `${uuid}:${type}` — compuesto a propósito, para que recapturar la misma
//   foto de la misma visita reemplace la fila en vez de duplicarla.
// type: 'visit' (rostro) | 'credential' (identificación)
// syncStatus: 'pending' | 'failed'
db.version(6).stores({
  pending_visit_photos: 'key, uuid, syncStatus',
})

/**
 * Tipos de foto admitidos por la cola, mapeados al endpoint del backend que
 * los recibe y al campo del registro de visita donde vive su metadata
 * (`{ id, name }`, ver `Visit#to_json` en CorporateService).
 */
export const VISIT_PHOTO_TYPES = {
  visit: { endpoint: 'visit/photo', visitField: 'photo' },
  credential: { endpoint: 'visit/credential_photo', visitField: 'credential_photo' },
}

/**
 * Persiste una foto capturada en la cola de subida pendiente.
 *
 * `source` puede ser tanto un data URL (`PhotoDialog` usa
 * `webcam.getScreenshot()`) como un blob URL (`IDDialog` usa
 * `URL.createObjectURL`); `fetch` resuelve ambos. Debe llamarse ANTES de
 * limpiar el formulario, porque `IDDialog` revoca su blob URL al recapturar y
 * un URL revocado ya no se puede leer.
 *
 * @param {string} uuid   uuid de la visita dueña de la foto
 * @param {'visit'|'credential'} type
 * @param {string} source data URL o blob URL de la imagen capturada
 * @returns {Promise<boolean>} `true` si quedó encolada
 */
export async function enqueueVisitPhoto(uuid, type, source) {
  if (!uuid || !source || !VISIT_PHOTO_TYPES[type]) return false

  try {
    const response = await fetch(source)
    const blob = await response.blob()
    const mimeType = blob.type || 'image/jpeg'
    const extension = mimeType.split('/')[1] || 'jpg'

    await db.pending_visit_photos.put({
      key: `${uuid}:${type}`,
      uuid,
      type,
      blob,
      mimeType,
      fileName: `${uuid}-${type}.${extension}`,
      createdAt: new Date().toISOString(),
      syncStatus: 'pending',
      syncError: null,
      pushAttempts: 0,
    })
    return true
  } catch (error) {
    console.error(`[db] No se pudo encolar la foto ${type} de la visita ${uuid}:`, error)
    return false
  }
}

/**
 * Elimina de la cola todas las fotos pertenecientes a las visitas indicadas.
 * Se usa al descartar visitas en cuarentena (ver `LoadPage`) para que no
 * queden blobs huérfanos ocupando espacio en IndexedDB.
 *
 * @param {string[]} uuids
 */
export async function deletePendingVisitPhotos(uuids = []) {
  if (!uuids.length) return
  await db.pending_visit_photos.where('uuid').anyOf(uuids).delete()
}

/**
 * Fecha (YYYY-MM-DD, hora local) que marca el inicio de la ventana de
 * relevancia offline: hoy - SYNC_WINDOW_DAYS. Usada tanto por `SyncEngine`
 * (parámetro `since_date` / rango de invitaciones) como por `pruneOldRecords`
 * para que ambos compartan exactamente el mismo límite.
 *
 * @returns {string} p.ej. '2026-05-02'
 */
export function getSyncWindowStartDate() {
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - SYNC_WINDOW_DAYS)
  return formatDateToISOShort(cutoff)
}

/**
 * Fecha (YYYY-MM-DD, hora local) que marca el fin de la ventana de relevancia
 * para invitaciones: hoy + SYNC_WINDOW_DAYS. Las invitaciones tienen
 * `invitation_date` futura (agendadas con anticipación), a diferencia de las
 * visitas que solo miran hacia atrás.
 *
 * @returns {string} p.ej. '2026-10-29'
 */
export function getSyncWindowEndDate() {
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() + SYNC_WINDOW_DAYS)
  return formatDateToISOShort(cutoff)
}

/**
 * Purga de la réplica local los registros fuera de la ventana de relevancia
 * (`SYNC_WINDOW_DAYS`), pensada para ejecutarse tras cada sync exitoso.
 *
 * Reglas de seguridad:
 *  - `visits`: solo se purgan registros `syncStatus === 'synced'` con
 *    `entry_time` anterior al inicio de la ventana. Las visitas
 *    `pending`/`failed` nunca se borran, sin importar su antigüedad, para no
 *    perder datos que el backend aún no confirmó.
 *  - `invitations`: se purgan las que caen fuera de [hoy-90d, hoy+90d] por
 *    `invitation_date`, pero nunca las de **hoy**, incluso si por alguna
 *    razón coincidiera el ciclo de purga justo en el límite de la ventana —
 *    son las únicas que garantizan el autocompletado offline.
 *  - `pending_visit_photos`: solo se purgan las huérfanas, es decir las que
 *    apuntan a un uuid que ya no existe en `db.visits` (su visita fue purgada
 *    o descartada). Nunca se borra una foto cuya visita sigue viva, por
 *    antigua que sea: el blob es el único original que existe.
 *
 * El resto del historial sigue disponible en línea vía búsqueda puntual
 * indexada en el backend (`/visits/search`, `/invitations` con rango).
 */
export async function pruneOldRecords() {
  const windowStartTime = new Date(getSyncWindowStartDate()).getTime()
  const windowEndTime = new Date(getSyncWindowEndDate()).getTime()
  const todayStr = formatDateToISOShort(new Date())

  const visits = await db.visits.toArray()
  const staleVisitUuids = visits
    .filter(v => (
      v.syncStatus === 'synced' &&
      v.entry_time &&
      new Date(v.entry_time).getTime() < windowStartTime
    ))
    .map(v => v.uuid)
  if (staleVisitUuids.length) {
    await db.visits.bulkDelete(staleVisitUuids)
  }

  const invitations = await db.invitations.toArray()
  const staleInvitationIds = invitations
    .filter(inv => {
      if (!inv.invitation_date || inv.invitation_date === todayStr) return false
      const time = new Date(inv.invitation_date).getTime()
      return time < windowStartTime || time > windowEndTime
    })
    .map(inv => inv.id)
  if (staleInvitationIds.length) {
    await db.invitations.bulkDelete(staleInvitationIds)
  }

  // Se relee `db.visits` porque los borrados de arriba ya se aplicaron: una
  // foto cuya visita acaba de purgarse cuenta como huérfana en este mismo ciclo.
  const liveVisitUuids = new Set(await db.visits.toCollection().primaryKeys())
  const pendingPhotos = await db.pending_visit_photos.toArray()
  const orphanPhotoKeys = pendingPhotos
    .filter(photo => !liveVisitUuids.has(photo.uuid))
    .map(photo => photo.key)
  if (orphanPhotoKeys.length) {
    await db.pending_visit_photos.bulkDelete(orphanPhotoKeys)
  }
}

/**
 * Busca localmente la visita MÁS RECIENTE para una identificación, replicando
 * la semántica de `GET /visits/search` en el backend
 * (`Visit.identification(...).order(entry_time: :desc).first`).
 *
 * Necesario porque `db.visits` no tiene un índice por `identification`, así
 * que un `.filter().first()` ingenuo devuelve el primer registro en orden de
 * clave primaria (`uuid`) — esencialmente arbitrario respecto a `entry_time`.
 * Si una persona tiene más de una visita dentro de la ventana local de 90
 * días (p.ej. una cerrada hace días y otra abierta hoy), eso puede devolver
 * la visita equivocada y hacer que `VisitForm` no detecte una visita abierta
 * que sí debería disparar el modal de checkout (ver `evaluateIsAlreadyCheckin`).
 *
 * @param   {string} identification
 * @returns {Promise<object|null>} La visita con `entry_time` más reciente, o `null` si no hay ninguna
 */
export async function findLatestVisitByIdentification(identification) {
  const matches = await db.visits.filter(v => v.identification === identification).toArray()
  if (!matches.length) return null

  return matches.reduce((latest, visit) =>
    new Date(visit.entry_time) > new Date(latest.entry_time) ? visit : latest
  )
}

/**
 * Busca localmente la visita MÁS RECIENTE para una placa.
 *
 * Misma razón que `findLatestVisitByIdentification`: `db.visits` no indexa
 * `license_plate`, así que `.filter().first()` devolvería un uuid arbitrario
 * y podría autocompletar (o abrir checkout) con una visita que no es la última.
 * La comparación es en mayúsculas porque el formulario ya normaliza la placa
 * al escribirla.
 *
 * @param   {string} licensePlate
 * @returns {Promise<object|null>} La visita con `entry_time` más reciente, o `null` si no hay ninguna
 */
export async function findLatestVisitByLicensePlate(licensePlate) {
  const plate = String(licensePlate || '').trim().toUpperCase()
  if (!plate) return null

  const matches = await db.visits
    .filter(v => String(v.license_plate || '').trim().toUpperCase() === plate)
    .toArray()
  if (!matches.length) return null

  return matches.reduce((latest, visit) =>
    new Date(visit.entry_time) > new Date(latest.entry_time) ? visit : latest
  )
}

/**
 * Busca localmente la invitación cuyo `unique_identificator` coincide con el
 * código embebido en el QR escaneado (prefijo `parso_`, ver `DEFAUTL_QR_START_CODE`
 * en `variables.js`). Es el equivalente offline de `GET /invitations/:code`
 * (ver `onSearchByQRInvitations` en `VisitForm.js`).
 *
 * @param   {string} uniqueIdentificator Texto completo decodificado del QR
 * @returns {Promise<object|null>} La invitación local, o `null` si no hay match
 */
export async function findInvitationByQRCode(uniqueIdentificator) {
  const code = String(uniqueIdentificator || '').trim()
  if (!code) return null
  const todayStr = formatDateToISOShort(new Date())
  const matches = await db.invitations.where('unique_identificator').equals(code).toArray()
  return matches.find(invitation =>
    String(invitation.invitation_date || '').split('T')[0] === todayStr
  )
}

/**
 * Wipes local data from every table in a single atomic transaction, except
 * for visits and invitations that were never confirmed by the server.
 *
 * Called on logout to ensure no sensitive visit, invitation, employee, or
 * blacklist data remains on the device after the session ends. Visits with
 * `syncStatus` `'pending'` or `'failed'` are moved into `quarantined_visits`,
 * and invitations in the same states into `quarantined_invitations`, instead
 * of being deleted, so that data isn't lost — the next login flow
 * (`LoadPage` + `PendingVisitsModal`/`PendingInvitationsModal`) asks the user
 * whether to resync or discard them.
 *
 * `pending_visit_photos` no necesita tabla de cuarentena propia: basta con
 * conservar las filas de las visitas que sí se ponen en cuarentena. Como
 * `LoadPage.handleSyncQuarantined` restaura la visita con el MISMO uuid, la
 * foto vuelve a quedar emparejada sola y el siguiente sync la sube. Las fotos
 * de visitas ya sincronizadas se borran junto con el resto de los datos.
 *
 * The database schema (tables & indexes) is preserved so the next login can
 * repopulate via the sync engine without needing a schema migration.
 */
export async function clearLocalDatabase() {
  await db.transaction('rw', db.tables, async () => {
    const quarantinedAt = new Date().toISOString()

    const unsyncedVisits = await db.visits.where('syncStatus').anyOf(UNSYNCED_VISIT_STATUSES).toArray()
    if (unsyncedVisits.length) {
      await db.quarantined_visits.bulkPut(unsyncedVisits.map(v => ({ ...v, quarantinedAt })))
    }

    const unsyncedInvitations = await db.invitations.where('syncStatus').anyOf(UNSYNCED_INVITATION_STATUSES).toArray()
    if (unsyncedInvitations.length) {
      await db.quarantined_invitations.bulkPut(unsyncedInvitations.map(inv => ({ ...inv, quarantinedAt })))
    }

    const quarantinedUuids = new Set(unsyncedVisits.map(v => v.uuid))
    const photosToDiscard = await db.pending_visit_photos
      .filter(photo => !quarantinedUuids.has(photo.uuid))
      .primaryKeys()

    await Promise.all([
      ...db.tables
        .filter(table => (
          table.name !== 'quarantined_visits' &&
          table.name !== 'quarantined_invitations' &&
          table.name !== 'pending_visit_photos'
        ))
        .map(table => table.clear()),
      db.pending_visit_photos.bulkDelete(photosToDiscard),
    ])
  })
}

export default db
